import { useEffect, useRef } from 'react';
import { CommonDI, LoggerType } from '@headless-workspace/domain/common/DI';
import { DateHelpers } from '@headless-workspace/utils';

const AUDIO_CONTAINER_SELECTOR = '#ekoo-container';
const AUDIO_MISSING_ERROR = 'Could not find audio element in the Ekoo container.';

export const useAudioPlayer = (): void => {
    const eventListenerReferences = useRef<[HTMLAudioElement, string, EventListener][]>([]);

    const waitForAudioDuration = (audioElement: HTMLAudioElement) => {
        const originalDuration = document.querySelector('.ekoo-slidecontainer-duration') as HTMLElement;
        if (originalDuration) {
            originalDuration.style.display = 'none';
        }

        const container = document.querySelector('.ekoo-slidecontainer');
        const slider = document.querySelector('.ekoo-slidecontainer-range');

        const description = document.querySelector('.ekoo-informations-description');
        const mainText = document.querySelector('.ekoo-widget-container-text-main-content');

        if (description && mainText) {
            description.textContent = mainText.textContent;
        }

        if (container && slider) {
            const currentTimeEl = document.createElement('div');
            currentTimeEl.className = 'current-time';
            currentTimeEl.style.color = 'var(--text-tertiary)';
            currentTimeEl.style.fontSize = '14px';
            currentTimeEl.style.marginRight = '8px';
            currentTimeEl.textContent = '00:00'; // Initial value

            const totalTimeEl = document.createElement('div');
            totalTimeEl.className = 'total-time';
            totalTimeEl.style.color = 'var(--text-tertiary)';
            totalTimeEl.style.fontSize = '14px';
            totalTimeEl.style.marginLeft = '8px';

            container.insertBefore(currentTimeEl, slider);
            container.insertBefore(totalTimeEl, slider.nextSibling);

            const updateCurrentTime = () => {
                currentTimeEl.textContent = DateHelpers.formatTimeDuration(audioElement.currentTime);
            };
            const updateTotalTime = () => {
                totalTimeEl.textContent = DateHelpers.formatTimeDuration(audioElement.duration);
            };

            audioElement.addEventListener('timeupdate', updateCurrentTime);
            audioElement.addEventListener('loadedmetadata', updateTotalTime);

            eventListenerReferences.current.push([audioElement, 'timeupdate', updateCurrentTime]);
            eventListenerReferences.current.push([audioElement, 'loadedmetadata', updateTotalTime]);
        }
    };

    useEffect(() => {
        if (typeof document === 'undefined') {
            return;
        }

        const eventListenerList = eventListenerReferences.current;

        const audioMutationObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((addedNode) => {
                    const htmlElement = addedNode.nodeType === Node.ELEMENT_NODE ? (addedNode as HTMLElement) : null;
                    if (htmlElement && htmlElement.className === 'ekoo-standalone') {
                        const audioElement = htmlElement.querySelector('audio');
                        if (audioElement) {
                            waitForAudioDuration(audioElement);
                        }
                    }
                });
            });
        });

        const audioContainer = document.querySelector(AUDIO_CONTAINER_SELECTOR);
        if (!audioContainer) {
            return CommonDI.get(LoggerType).error(AUDIO_MISSING_ERROR);
        }

        audioMutationObserver.observe(audioContainer, {
            childList: true,
            subtree: true,
        });

        return () => {
            eventListenerList.forEach(([element, type, handler]) => {
                element.removeEventListener(type, handler);
            });
            audioMutationObserver.disconnect();
        };
    }, []);
};
